import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Card, Pill, ScoreBar } from '../ui';
import { adminApi, DIMENSIONS, EVIDENCE_META, PROGRAM_NAMES, STATE_META } from '../../../pages/journey/journeyApi';
import { ConsultationForm } from './ConsultationForm';
import { RecommendationEditor } from './RecommendationEditor';
import { PlacementPanel } from './PlacementPanel';
import { EnrollmentAnswers } from './EnrollmentAnswers';
import { KickoffNotes } from './KickoffNotes';

const BookingStatus = ({ s, onSaved }) => {
  const c = s.consultation;
  const mockPay = async () => {
    try { await adminApi(`/students/${s.id}/consultation/mock-paid`, { method: 'POST' }); toast.success('Marked as paid · ₹499 (mock)'); onSaved(); }
    catch (e) { toast.error(e.message); }
  };
  return (
    <Card className="flex flex-wrap items-center justify-between gap-3" data-testid="admin-booking-status">
      <div>
        <p className="font-outfit font-semibold text-[#0b1f4d]">Career Clarity Consultation · ₹499</p>
        <p className="text-sm text-slate-500" data-testid="admin-booking-status-text">
          {c.status === 'not_booked' ? 'Not booked or paid yet' : `${c.status === 'completed' ? 'Completed' : 'Booked'} · ₹${c.paid ?? 499} paid${c.booking_source === 'mock' ? ' (mock)' : c.booking_source === 'website' ? ' on the website' : ''}${c.payment_ref ? ` · ${c.payment_ref}` : ''}`}
        </p>
      </div>
      {c.status === 'not_booked' && (
        <button onClick={mockPay} data-testid="admin-mock-paid-btn" className="text-sm font-semibold border border-amber-300 bg-amber-50 text-amber-800 px-4 py-2 rounded-full hover:bg-amber-100">
          Mark as paid (test)
        </button>
      )}
    </Card>
  );
};

const Learning = ({ d }) => (
  <Card data-testid="admin-student-learning">
    <p className="font-outfit font-semibold text-[#0b1f4d] mb-3">Enrollment & learning</p>
    {!d.enrollments.length && <p className="text-sm text-slate-500">Not enrolled yet.</p>}
    {d.enrollments.map((e) => <p key={e.id} className="text-sm">{e.program_name} · ₹{e.amount} · {e.payment.mode} {e.payment.ref} · reviews used {e.support.milestone_reviews_used}/{e.support.milestone_reviews}, specialist {e.support.specialist_reviews_used}/{e.support.specialist_reviews}</p>)}
    {d.pathways.map((pw) => (
      <div key={pw.program_id} className="mt-4"><p className="text-xs uppercase tracking-widest text-teal-700 mb-1">{PROGRAM_NAMES[pw.program_id]} · {pw.progress}%{pw.skipped ? ` (${pw.skipped} skipped)` : ''} · level {pw.level || 'pending diagnostic'}</p>
        {pw.items.map((i) => <div key={i.mission.id} className="flex justify-between text-sm py-1"><span>{i.mission.title}</span><Pill className={STATE_META[i.state].cls}>{STATE_META[i.state].label}{i.attempts.length ? ` · ${i.attempts.map((a) => a.score ?? '…').join('→')}` : ''}</Pill></div>)}
      </div>))}
    {d.competencies.length > 0 && <div className="mt-4 flex flex-wrap gap-1.5">{d.competencies.map((c) => <Pill key={c.id} className={EVIDENCE_META[c.evidence].cls}>{c.name}: {EVIDENCE_META[c.evidence].label}</Pill>)}</div>}
    {d.checkins.length > 0 && <p className="text-sm mt-4">Check-ins: {d.checkins.map((c) => `W${c.week} ${c.status}`).join(' · ')}{d.checkins[0].blockers ? ` · latest blocker: "${d.checkins[0].blockers}"` : ''}</p>}
  </Card>
);

export const AdminStudentDetail = ({ studentId, catalog, onChange }) => {
  const [d, setD] = useState(null);
  const load = useCallback(() => adminApi(`/students/${studentId}`).then(setD).catch((e) => toast.error(e.message)), [studentId]);
  useEffect(() => { load(); }, [load]);
  if (!d) return <div className="h-40 bg-white rounded-2xl animate-pulse" />;
  const s = d.student;
  const reload = () => { load(); onChange(); };
  return (
    <div className="space-y-5" data-testid="admin-student-detail">
      <Card>
        <p className="font-outfit text-xl font-bold text-[#0b1f4d]" data-testid="admin-student-name">{s.full_name}</p>
        <p className="text-sm text-slate-500">{s.registration.current_status} · {s.registration.education} · {s.city}</p>
        <p className="text-sm mt-2">Goal: {s.registration.primary_goal} · Challenge: {s.registration.biggest_challenge} · Timeline: {s.registration.timeline}</p>
        <div className="grid sm:grid-cols-4 gap-4 mt-4">{DIMENSIONS.map(([k, l]) => <ScoreBar key={k} label={l} value={s.readiness[k] ?? 0} />)}</div>
        <div className="flex flex-wrap gap-1.5 mt-4">{(s.self_reported_skills || []).map((k) => <Pill key={k.skill} className="bg-amber-50 text-amber-700">{k.skill}: {k.level} ({k.evidence})</Pill>)}</div>
      </Card>
      <BookingStatus s={s} onSaved={reload} />
      <div className="grid xl:grid-cols-[3fr_2fr] gap-5 items-start">
        <EnrollmentAnswers forms={d.enrollment_forms || []} />
        <KickoffNotes student={s} onSaved={load} />
      </div>
      <ConsultationForm key={`c-${s.consultation.status}`} student={s} catalog={catalog} onSaved={reload} />
      <RecommendationEditor key={`r-${s.recommendation?.generated_at || 'none'}-${s.consultation.status}`} student={s} onSaved={reload} />
      <Learning d={d} />
      <PlacementPanel d={d} onSaved={reload} />
    </div>
  );
};
